import constate from 'constate';
import { useCallback, useLayoutEffect, useState, useRef } from 'react';
import { getResizer } from '../services/resizer';
import { useServices } from './services-registry';
import { useDesign } from './editor-settings';
import { useAppState } from './app-state';
import { DeviceType } from '../types/host-sdk';
import { getDimensions } from '../services/dimensions';
import { useWixAnalytics } from './wix-analytics';
import { useCorvidApi } from './corvid-api';
import { SessionStorageKeys, useSessionStorage } from './useSessionStorage';

interface ProviderProps {
  initialValue?: boolean;
}

interface Context {
  isExpanded: boolean;
  setIsExpanded(boolean): void;
}

interface CollapseExpand {
  isExpanded: boolean;
  isCollapsed: boolean;
  collapse(withPopup?: boolean): void;
  expand(direction?: string, triggerBy?: string): void;
  toggle(direction?: string, triggerBy?: string): void;
  setRef(ref: HTMLElement): void;
}

const useContext = ({ initialValue = false }: ProviderProps): Context => {
  const [isExpanded, setIsExpanded] = useState(initialValue);
  return { isExpanded, setIsExpanded };
};

const useCollapseExpand_ = (context: Context): CollapseExpand => {
  const [ref, setRef] = useState<HTMLElement | undefined>(undefined);
  const { hostSdk, biLogger, experiments } = useServices();
  const design = useDesign();
  const { appState } = useAppState();
  const { trackExpandEvent, trackCollapseEvent } = useWixAnalytics();
  const corvidApi = useCorvidApi();
  const expandCallback = useRef<(() => void) | null>(null);
  const { setValue: setIsExpanded, getValue: getIsExpanded } =
    useSessionStorage(SessionStorageKeys.IsExpanded);

  const { withQab } = appState;
  const isMobile = appState.deviceType === DeviceType.Mobile;
  const isCollapsed = !context.isExpanded;
  const expandChatFromStorageValue = experiments.enabled(
    'specs.chat.expandChatFromStorageValue',
  );

  const collapse = useCallback(async () => {
    const dimensions = getDimensions(design, isMobile);
    const resizer = getResizer(withQab, isMobile, hostSdk, dimensions);

    await resizer.collapse(appState.showMessagePopup);
    if (expandChatFromStorageValue) {
      setIsExpanded(false);
    }

    if (expandCallback.current) {
      window.removeEventListener('resize', expandCallback.current);
      expandCallback.current = null;
    }

    if (context.isExpanded) {
      trackCollapseEvent();
      corvidApi.reportWidgetCollapsed();
    }

    context.setIsExpanded(false);
  }, [
    design,
    isMobile,
    withQab,
    hostSdk,
    appState.showMessagePopup,
    expandChatFromStorageValue,
    context,
    setIsExpanded,
    trackCollapseEvent,
    corvidApi,
  ]);

  const expand = useCallback(
    async (direction?: string, triggerBy?: string) => {
      const dimensions = getDimensions(design, isMobile);
      const resizer = getResizer(withQab, isMobile, hostSdk, dimensions);

      const callback = () => resizer.expand();
      expandCallback.current = callback;
      window.addEventListener('resize', callback); // Bad practice, but needed—hostSdk returns wrong screenSize otherwise

      await resizer.expand();
      if (expandChatFromStorageValue) {
        setIsExpanded(true);
      }

      if (!context.isExpanded) {
        trackExpandEvent();
        corvidApi.reportWidgetExpand();

        if (direction && triggerBy) {
          biLogger.expandChatEvent({
            direction,
            triggerBy,
            host: appState.host,
          });
        }
      }

      context.setIsExpanded(true);
    },
    [context, corvidApi, trackExpandEvent, appState.host, setIsExpanded],
  );

  const toggle = useCallback(
    (direction?: string, triggerBy?: string) => {
      context.isExpanded ? collapse() : expand(direction, triggerBy);
    },
    [context.isExpanded, collapse, expand],
  );

  useLayoutEffect(() => {
    const resizeHost = () => {
      const dimensions = ref?.getBoundingClientRect();
      const adjustDimensions = getDimensions(design, isMobile, dimensions);
      const resizer = getResizer(withQab, isMobile, hostSdk, adjustDimensions);

      const isExpandedFromStorage = expandChatFromStorageValue
        ? getIsExpanded()
        : false;

      if (!appState.isVisible) {
        resizer.hide();
      } else if (isCollapsed && !isExpandedFromStorage) {
        void resizer.collapse(appState.showMessagePopup);
      } else {
        void resizer.expand();
      }
    };

    const observer = new MutationObserver(() => resizeHost());

    if (ref) {
      observer.observe(ref, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
    }

    resizeHost();
    return () => {
      observer.disconnect();
    };
  }, [
    ref,
    isCollapsed,
    withQab,
    isMobile,
    hostSdk,
    design,
    appState.isVisible,
    appState.showMessagePopup,
    getIsExpanded,
  ]);

  return {
    isExpanded: context.isExpanded,
    isCollapsed,
    collapse,
    expand,
    toggle,
    setRef,
  };
};

export const [CollapseExpandProvider, useCollapseExpand] = constate(
  useContext,
  useCollapseExpand_,
);
